import { useMutation } from '@tanstack/react-query';
import cx from 'classnames';
import { useAtom } from 'jotai';
import Cookies from 'js-cookie';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import {
  ComponentProps,
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CheckList from '../../modules/checklist';
import AvailablePaymentOptions from '../../modules/payment-methods/available-payment-options';
import { useAppContext } from '../../providers/app-context';
import BPImage from '../../ui/image';
import NoRiskInfo from './no-risk-info';
import { monthsToYears } from '../../../utils/seo/months-to-years';

const AlmaWidget = dynamic(() => import('../../providers/alma'), {
  ssr: false,
});

const KlarnaWidget = dynamic(() => import('../../providers/klarna'), {
  ssr: false,
});

const ShippingCostInfo = dynamic(
  () => import('../../modules/shipping-cost-info'),
  { ssr: false }
);

const BPartsPurchaseInfo: FunctionComponent<{
  productDetail: TProductDetail;
  hasEurop: boolean;
  isSold?: boolean;
  isReservated?: boolean;
}> = ({ productDetail, hasEurop, isSold, isReservated }) => {
  const { locale = 'en' } = useRouter();
  const { shippingLocation, detailedPrice: hasDetailedPrice } = useAppContext();
  const { t } = useTranslation();
  const [showMobile, setShowMobile] = useState(false);
  const ref = useRef();
  const inStock = productDetail.status === 'STO';
  const checkItems: ComponentProps<typeof CheckList>['items'] = [];
  const isUkWebsite = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');

  const warrantyInYears = productDetail?.warranty
    ? monthsToYears(productDetail.warranty)
    : undefined;

  const shippingLocationCountry = useMemo(() => {
    switch (shippingLocation) {
      case 1:
      case 2:
      case 3:
        return t('home:banner.countries.portugal');
      case 4:
      case 5:
      case 6:
        return t('home:banner.countries.spain');
      case 11:
        return t('home:banner.countries.france');
      case 13:
        return t('home:banner.countries.germany');
      case 23:
        return t('home:banner.countries.italy');
      case 25:
        return t('home:banner.countries.belgium');
      case 26:
        return t('home:banner.countries.austria');
      default:
        switch (locale) {
          case 'pt':
            return t('home:banner.countries.portugal');
          case 'es':
            return t('home:banner.countries.spain');
          case 'fr':
            return t('home:banner.countries.france');
          case 'de':
            return t('home:banner.countries.germany');
          case 'it':
            return t('home:banner.countries.italy');
          case 'nl':
            return t('home:banner.countries.belgium');
          case 'at':
            return t('home:banner.countries.austria');
          case 'en':
            return t('home:banner.countries.european');
          default:
            return t('home:banner.countries.european');
        }
    }
  }, [locale, shippingLocation, t]);

  useEffect(() => {
    const watcher: IntersectionObserverCallback = (entries) => {
      setShowMobile(!entries[0].intersectionRatio);
    };

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    };

    let observer: IntersectionObserver;

    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(watcher, options);

      ref?.current && observer.observe(ref?.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [ref]);

  if (productDetail.delivery_day_start && productDetail.delivery_day_end) {
    checkItems.push(
      ...[
        {
          key: 'product:checklist.delivery',
          icon: (
            <BPImage
              src={`${process.env.PUBLIC_PREFIX}/svg/icons/clock.svg`}
              width={16}
              height={16}
              className={cx('h-4 w-4')}
              alt="delivery icon"
            />
          ),
          content: (
            <Trans
              i18nKey="product:checklist.delivery"
              components={{ b: <b /> }}
              values={{
                from: productDetail.delivery_day_start,
                to: productDetail.delivery_day_end,
              }}
            />
          ),
        },
      ]
    );
  }

  if (productDetail.warranty && hasEurop) {
    checkItems.splice(2, 0, {
      key: warrantyInYears
        ? warrantyInYears === 1
          ? 'product:checklist.warranty-europ_year'
          : 'product:checklist.warranty-europ_years'
        : 'product:checklist.warranty-europ',
      icon: (
        <BPImage
          src={`${process.env.PUBLIC_PREFIX}/svg/icons/shield-plus.svg`}
          width={16}
          height={16}
          className={cx('h-4 w-4')}
          alt="warranty plus icon"
        />
      ),
      content: (
        <Trans
          i18nKey={
            warrantyInYears
              ? warrantyInYears === 1
                ? 'product:checklist.warranty-europ_year'
                : 'product:checklist.warranty-europ_years'
              : 'product:checklist.warranty-europ'
          }
          components={{ b: <b /> }}
          values={{
            warranty: warrantyInYears
              ? warrantyInYears
              : productDetail.warranty,
          }}
        />
      ),
      modal: {
        title: t('common:extra-info'),
        content: isUkWebsite ? t('product:europ_modal_uk') : t('product:europ_modal')
      }
    });
  }

  if (productDetail.import_duties_message) {
    checkItems.splice(1, 0, {
      key: 'product:checklist.warranty',
      icon: (
        <BPImage
          src={`${process.env.PUBLIC_PREFIX}/svg/icons/duties_included.svg`}
          width={16}
          height={16}
          className={cx('h-4 w-4')}
          alt="import duties icon"
        />
      ),
      content: (
        <Trans
          i18nKey="product:checklist.import_duties_message"
          components={{ b: <b /> }}
        />
      ),
    });
  }

  return (
    <div
      className={cx(
        'max-lg:container max-lg:mx-auto',
        'max-md:px-4 max-lg:py-4',
        isSold
          ? 'max-lg:bg-red-10 '
          : isReservated
            ? 'max-lg:bg-yellow-10'
            : productDetail.in_cart
              ? 'max-lg:bg-greem-10'
              : 'max-lg:bg-neutral-5 '
      )}
    >
      <div
        className={cx(
          'grid p-4 lg:py-2 lg:border-l-neutral-80 max-lg:bg-white lg:shadow-[-7px_0px_8px_-8px_rgba(0,0,0,0.1)]'
        )}
      >
        <div className={cx('grid', 'gap-4', 'lg:grid-cols-1')}>
          <div className={''}>
            <ShippingCostInfo
              productDetail={productDetail}
              shippingCostVisible={true}
              iconSide="left"
              isPP={true}
            />
          </div>
          {productDetail?.warranty && (
            <div
              className={cx(
                'flex items-center gap-4 text-left',
                'text-green-100',
                'text-base',
                'font-bold',
                'pl-0'
              )}
            >
              <BPImage
                src={`${process.env.PUBLIC_PREFIX}/svg/icons/warranty-icon.svg`}
                width={16}
                height={16}
                className={cx('h-4 w-4')}
                alt="warranty icon"
              />
              {warrantyInYears
                ? warrantyInYears === 1
                  ? t('product:warranty.year_warranty', {
                    qtt: warrantyInYears,
                  })
                  : t('product:warranty.years_warranty', {
                    qtt: warrantyInYears,
                  })
                : t('product:warranty.months_warranty', {
                  qtt: productDetail.warranty,
                })}
            </div>
          )}
          <NoRiskInfo />
          <div className={cx('lg:px-0', 'grid grid-cols-[28px_1fr]')}>
            <div className="mt-1">
              <BPImage
                src={`${process.env.PUBLIC_PREFIX}/svg/icons/card.svg`}
                width={16}
                height={16}
                className={cx('h-4 w-4')}
                alt="payment methods icon"
              />
            </div>
            <p className="text-sm pt-1">
              <Trans
                i18nKey="product:accepted_payment_methods"
                components={{
                  b: <b />,
                }}
                values={{ country: shippingLocationCountry }}
              />
            </p>
          </div>
          <div className={cx('pl-7')}>
            <AvailablePaymentOptions isPP={true} />
          </div>
          {inStock &&
            !isUkWebsite &&
            productDetail?.klarna &&
            productDetail?.pricing && (
              <KlarnaWidget
                langCountry={productDetail?.klarna.locale}
                amount={
                  productDetail.pricing.price_in_default_currency
                    ? productDetail.pricing.price_in_default_currency
                    : productDetail.pricing.price_final
                }
              />
            )}
          <div
            className={cx('flex flex-col', {
              'mt-4': productDetail?.alma,
              'mt-4 lg:mt-0': !productDetail?.alma,
            })}
          >
            {inStock && productDetail?.alma?.amount && (
              <div
                className={cx(
                  'bg-blue-10',
                  'p-4',
                  'max-w-full',
                  'overflow-hidden'
                )}
              >
                <div
                  className={cx(
                    'font-bold',
                    'text-sm',
                    'text-center',
                    'leading-5',
                    'tracking-wider'
                  )}
                >
                  <p
                    className={cx(
                      'font-bold',
                      'text-sm',
                      'text-center',
                      'leading-5'
                    )}
                  >
                    {t('product:alma.title')}
                  </p>
                  <p
                    className={cx(
                      'mt-2',
                      'mb-3',
                      'text-sm',
                      'text-center',
                      'leading-5',
                      ''
                    )}
                  >
                    {t('product:alma.description')}
                  </p>
                  {productDetail?.alma && (
                    <AlmaWidget
                      amount={productDetail?.pricing?.price_in_default_currency}
                      merchant_key={productDetail.alma}
                    />
                  )}
                  {t('product:alma.description')}
                </div>
                {productDetail?.alma?.amount && (
                  <AlmaWidget
                    amount={productDetail.alma.amount}
                    merchant_key={{
                      key: productDetail.alma.key,
                      env: productDetail.alma.env,
                    }}
                  />
                )}
              </div>
            )}
            <CheckList background={false} items={checkItems} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BPartsPurchaseInfo;
